import { css } from "@compiled/react";
import { FormattedMessage, useIntl } from "react-intl";

import { media } from "@atlaskit/primitives/responsive";

import { ContentBlock } from "@/components/wpl/content-block/src";
import { DEFAULT_VALUES } from "@/libraries/web-framework";

const integrationStyles = css({
    "alignItems": "center",
    "display": "flex",
    "justifyContent": "center",
    "padding": "192px 0",
    "backgroundColor": "#ffffff",

    [media.below.xs]: {
        backgroundImage: `url(https://wac-cdn.atlassian.com/dam/jcr:4edf08c6-db6f-415f-b5b0-58307b033bfa/integrations-upper-320.svg?cdnVersion=1194), 
            url(https://wac-cdn.atlassian.com/dam/jcr:c2559fbc-9186-4d97-91d8-9874220f5bce/integrations-lower-320.svg?cdnVersion=1194)`,
        backgroundPosition: "center top, center bottom",
        backgroundRepeat: "no-repeat, no-repeat",
        backgroundSize: "70%, 65%",
        flexDirection: "column",
        padding: "142px 0"
    },
    [media.only.xs]: {
        backgroundImage: `url(https://wac-cdn.atlassian.com/dam/jcr:4edf08c6-db6f-415f-b5b0-58307b033bfa/integrations-upper-320.svg?cdnVersion=1194), 
            url(https://wac-cdn.atlassian.com/dam/jcr:c2559fbc-9186-4d97-91d8-9874220f5bce/integrations-lower-320.svg?cdnVersion=1194)`,
        backgroundPosition: "center top, center bottom",
        backgroundRepeat: "no-repeat, no-repeat",
        backgroundSize: "350px, 350px",
        flexDirection: "column"
    },
    [media.only.sm]: {
        backgroundImage: `url(https://wac-cdn.atlassian.com/dam/jcr:557ec237-312c-4945-9e96-9bb54e5fe954/integrations-left-720.svg?cdnVersion=1194), 
            url(https://wac-cdn.atlassian.com/dam/jcr:7ee516f1-496a-43ec-a580-0fb6d22f2f2a/integrations-right-720.svg?cdnVersion=1194)`,
        backgroundPosition: "left top 40px, right top 20px",
        backgroundRepeat: "no-repeat, no-repeat",
        backgroundSize: "27% auto, 27% auto",
        padding: "96px 0"
    },
    [media.only.md]: {
        backgroundImage: `url(https://wac-cdn.atlassian.com/dam/jcr:c62a1ec8-3eab-4bbd-ac12-dac4765e7178/integrations-left.svg?cdnVersion=1194), 
            url(https://wac-cdn.atlassian.com/dam/jcr:c14f553e-2abe-4b1e-90e6-2fbf013fdb0a/integrations-right.svg?cdnVersion=1194)`,
        backgroundPosition: "left top 40px, right top 50px",
        backgroundRepeat: "no-repeat, no-repeat",
        backgroundSize: "27% auto, 27% auto",
        padding: "96px 0"
    },
    [media.above.lg]: {
        backgroundImage: `url(https://wac-cdn.atlassian.com/dam/jcr:c62a1ec8-3eab-4bbd-ac12-dac4765e7178/integrations-left.svg?cdnVersion=1194),
            url(https://wac-cdn.atlassian.com/dam/jcr:c14f553e-2abe-4b1e-90e6-2fbf013fdb0a/integrations-right.svg?cdnVersion=1194)`,
        backgroundPosition: "left center, right top 30px",
        backgroundRepeat: "no-repeat, no-repeat",
        backgroundSize: "auto, auto"
    },

    ".integrations-content": {
        maxWidth: "720px",
        padding: "16px",
        width: "50%",

        [media.above.xs]: {
            h2: {
                fontSize: "2.25rem"
            },
            p: {
                fontSize: "1rem"
            }
        },
        [media.above.sm]: {
            h2: {
                marginBlockEnd: "24px"
            }
        },

        [media.below.sm]: {
            maxWidth: "1440px",
            width: "100%",
            h2: {
                "fontSize": "1.5rem",
                "lineHeight": "1.33333333",
                "webkit-font-smoothing": "antialiased",
                "moz-osx-font-smoothing": "grayscale",
                "color": "#253858",
                "marginBlockEnd": "14px",
                "fontWeight": "500"
            }
        },
        p: {
            "color": "#091e42",
            "marginBlockEnd": "24px",
            "fontWeight": "normal",
            "webkit-font-smoothing": "antialiased",
            "moz-osx-font-smoothing": "grayscale"
        },
        a: {
            "margin": "0px",
            "padding": "4px 10px",
            "fontWeight": "500",
            "height": "unset",
            "webkit-font-smoothing": "antialiased",
            "moz-osx-font-smoothing": "grayscale"
        }
    }
});

const IntegrationSection = () => {
    const intl = useIntl();

    return (
        <section css={integrationStyles}>
            <div className="integrations-content">
                <ContentBlock
                    headerText={intl.formatMessage({
                        id: "JiraSoftwarePageTemplate.integrationSection.header",
                        defaultMessage: "We believe in open relationships"
                    })}
                    bodyText={
                        <FormattedMessage
                            id="JiraSoftwarePageTemplate.integrationSection.body"
                            defaultMessage="If your team uses it, we integrate with it. Easily add your tools from the Atlassian Marketplace, keeping Jira as your central source of truth."
                            values={DEFAULT_VALUES}
                        />
                    }
                    headerType="h2"
                    isCentered
                    orientation="vertical"
                    ctaProps={{
                        buttonProps: {
                            text: intl.formatMessage({
                                id: "JiraSoftwarePageTemplate.integrationSection.cta",
                                defaultMessage: "Explore integrations"
                            }),
                            url: "https://marketplace.atlassian.com/",
                            type: "secondary",
                            size: "small",
                            dataEventProps: {
                                "data-event-component": "linkButton",
                                "data-event-container": "integrationSection",
                                "data-event": "clicked",
                                "data-product-key": "jiraSoftware"
                            }
                        }
                    }}
                />
            </div>
        </section>
    );
};

export default IntegrationSection;
